import {Component, OnInit} from '@angular/core';
import {GeneralListComponent} from '../../general/list/generalList.component';
import {Location} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService} from '../../../../shared/message.service';
import {UserService} from '../../../services/user.service';
import {User} from '../../../interfaces/user';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalWindowComponent} from '../../common/modal-window/modal-window';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {ServiceAnswer, ServiceAnswerAny} from '../../../../shared/service-answer';
import {ButtonMeta} from '../../common/buttons/buttonMeta';
import {TenantService} from '../../../services/tenant.service';
import {Tenant} from '../../../interfaces/tenant';
import {ServiceTools} from '../../../services/serviceTools';

/**
 * Class for user list.
 */
@Component({
  selector: 'app-usergroups',
  templateUrl: 'user-list.component.html',
  styleUrls: ['user-list.component.css'],
})
export class UserListComponent extends GeneralListComponent<User> implements OnInit {

  private static DEFAULT_TENANT_ID = 1;
  private static CONTROL_BUTTON_ID_REQUEST_PASSWORD = 'CONTROL_BUTTON_ID_REQUEST_PASSWORD';
  public tenants: Tenant[];
  public selectedTenant: Tenant;

  /**
   * Initializes the UserList.
   * @param service The userServce.
   * @param location The location.
   * @param router The router.
   * @param messagesService The MessageService.
   * @param route The route.
   * @param modalService The service used to openApplicationCacheDeleteDialog modal dialogs.
   */
  constructor(
    private service: UserService,
    private tenantService: TenantService,
    location: Location,
    router: Router,
    messagesService: MessageService,
    route: ActivatedRoute,
    modalService: NgbModal) {
    super(
      service,
      router,
      messagesService,
      route,
      modalService,
      {
        entityNameSingular: 'user',
        entityNamePlural: 'users',
        formUrl: 'user',
        listUrl: 'users',
        generalFormMetaName: 'user',
        tableColumns: [
          {id: 'name', title: 'Name'},
          {id: 'email', title: 'Email'}
        ]
      },
    );
  }

  /**
   * Opens a dialog where the selected user can be sent a request to change the password.
   */
  openPasswordChangeDialog(): void {
    const selected: User = this.selectedEntity;
    const idOfUser: string = this.getIdOfEntity(selected);
    const nameOfUser: string = selected['name'];
    const ngbModalRef: NgbModalRef = this.getModalService().open(ModalWindowComponent);
    this.increaseOpenView();
    ngbModalRef.componentInstance.text = `Request password change for user ${nameOfUser} (id: ${idOfUser})?`;
    ngbModalRef.componentInstance.title = 'Request password change';
    ngbModalRef.componentInstance.buttons = [
      {text: 'Cancel', id: 'cancel', focus: true},
      {text: 'Send request', id: 'send'},
    ];
    ngbModalRef.componentInstance.closed = async (buttonId: string) => {
      ngbModalRef.close();
      if (buttonId === 'send') {
        try {
          const serviceAnswer: ServiceAnswerAny = await this.service.requestPasswordChange(idOfUser, this.getSelectedTenantId());
          if (serviceAnswer.isOk) {
            this.messagesService.addSuccess(`Password change request sent successfully for user ${nameOfUser} (${idOfUser})`);
            return;
          } else {
            const errorMessage = `Password change request sent failed for user ${nameOfUser} (${idOfUser}). Backend returned HTTP code \
            ${serviceAnswer.errorCode}. Error message: ${serviceAnswer.errorMessage}.`;
            console.error(errorMessage);
            this.messagesService.addDanger(errorMessage);
          }
        } catch (error) {
          const message = `Unexpected error when sending password change request for user ${nameOfUser} (${idOfUser}): \
          ${JSON.stringify(error)}.`;
          console.error(message);
          this.messagesService.addDanger(message);
        }
      }
    };
    ngbModalRef.result.then((result) => {
      this.decreaseOpenView();
    }, (reason) => {
      this.decreaseOpenView();
    });
  }

  /**
   * Inits by calling super init and then fetching tenants.
   */
  async ngOnInit(): Promise<void> {
    this.tenants = ServiceTools.getEntitiesFromFirstContext(
      await this.tenantService.getEntities(),
      this.messagesService,
      'tenants'
    );
    await super.ngOnInit();
    // this.tenants = await this.tenantService.getEntities();
    this.tenants.sort((tenantA: Tenant, tenantB: Tenant) => {
      return tenantA.id - tenantB.id;
    });
    this.selectTenant(
      this.tenants.find((tenant: Tenant) => tenant.id === UserListComponent.DEFAULT_TENANT_ID),
      false);
  }

  /**
   * Set the given tenant to selectedTenant.
   * @param tenant The tenant to be selected.
   */
  selectTenant(tenant: Tenant, fetchTenants: boolean = true): void {
    this.selectedTenant = tenant;
    if (fetchTenants) {
      this.fetchEntities();
    }
  }

  /**
   * Return rest parameters. TenantId parameter with value of selected tenant is added
   * to the values to be returned.
   */
  protected getRestParameters(): { key: string; value: string }[] {
    return [
      {
        key: 'tenantId',
        value: `${this.getSelectedTenantId()}`
      }
    ];
  }

  /**
   * Returns a id of user entity. Instead of 'id' field the id of the user is
   * taken from 'user_id' field. If the entity has no user_id then empty string
   * is returned.
   * @param entity The entity from which the id is taken.
   */
  protected getIdOfEntity(entity: User): string {
    return entity ? '' + entity['user_id'] : null;
  }

  /**
   * Returns the form url for given id. TenantId is added to url as parameter.
   * @param id The id of the entity whose form url is requested.
   */
  protected getViewUrl(id: number | string): string {
    return `${super.getViewUrl(id)}/?tenantId=${this.getSelectedTenantId()}`;
  }

  /**
   * When a control button has been pressed. Call super controlButtonPressed and
   * checks if the cache delete button has been pressed.
   * @param buttonId The id of the button.
   */
  controlButtonPressed(buttonId: string) {
    super.controlButtonPressed(buttonId);
    if (buttonId === UserListComponent.CONTROL_BUTTON_ID_REQUEST_PASSWORD) {
      this.openPasswordChangeDialog();
    }
  }

  /**
   * Returns array of button meta objects. These objects are used to create buttons.
   */
  public getButtonsMeta(): ButtonMeta[] {
    const buttonMeta: ButtonMeta[] = super.getButtonsMeta();
    buttonMeta.splice(2, 0,
      {
        disableWhenNoSelection: true,
        text: 'Request password change',
        type: 'general',
        id: UserListComponent.CONTROL_BUTTON_ID_REQUEST_PASSWORD
      });
    return buttonMeta;
  }

  /**
   * Returns the id of the selected tenant. If no tenant has been selected then the default
   * tenant id is returned.
   */
  public getSelectedTenantId(): number {
    return (this.selectedTenant ? this.selectedTenant.id : UserListComponent.DEFAULT_TENANT_ID);
  }

  /**
   * Returns the name of the selected tenant. If no tenant has been selected then an empty string
   * is returned.
   */
  public getSelectedTenantName(): string {
    return (this.selectedTenant ? this.selectedTenant.name : '');
  }
}
